import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, Select } from "@opendate-app/setlist";
import ConfirmAddOnDrawer from "../../ConfirmTicketing/components/AddOnDrawer";
import { blankAddOn, dynamicPricingProblem } from "../scheduleBuilder";
import { addOnToForm, fetchFeeQuote, formToAddOn, manualTaxIdsFrom } from "../confirmAdapters";
import PriceChangesSection, { pricePointProblems } from "./PriceChangesSection";
import ShowDetailOffset, { offsetsWithinAYear } from "./ShowDetailOffset";
import { toastError } from "../../../shared/toastHelper";

// Every rule the series enforces before an add-on can be saved.
export const addOnProblems = (draft, { takenNames = [], timedEntry = false, canUseDynamicPricing = false }) => {
  const paid = Number(draft.price) > 0;
  const pricePoints = paid ? draft.pricePoints || [] : [];
  const priceProblems = pricePointProblems(pricePoints);
  const minimum = Number(draft.minimumPerOrder);
  const maximum = Number(draft.maximumPerOrder);
  const name = draft.name.trim();
  const problems = {
    name: !name ? "missing" : takenNames.includes(name) ? "Another add-on already uses this name." : null,
    price: draft.price === "" ? "missing" : null,
    inventory: draft.inventory === "" ? "missing" : null,
    limits: draft.minimumPerOrder === "" || draft.maximumPerOrder === "" || minimum > maximum ? "limits" : null,
    tickets: draft.requireTicketType && draft.requiredTicketTypeNames.length === 0 ? "tickets" : null,
    window: Boolean(draft.startActivity) !== Boolean(draft.endActivity) ? "window" : null,
    offsets:
      offsetsWithinAYear(draft.startOffsetMinutes, draft.endOffsetMinutes) &&
      (timedEntry || offsetsWithinAYear(draft.scanStartOffsetMinutes, draft.scanEndOffsetMinutes))
        ? null
        : "offsets",
    scan:
      !timedEntry &&
      (Boolean(draft.scanStartActivity) !== Boolean(draft.scanEndActivity) ||
        (draft.scanStartActivity && draft.scanStartActivity === draft.scanEndActivity))
        ? "Scanning has to open and close on different show details."
        : null,
    priceChanges: Object.values(priceProblems).some(Boolean) ? "priceChanges" : null,
    dynamicPricing: canUseDynamicPricing && paid ? dynamicPricingProblem({ ...draft, pricePoints }) : null,
  };
  return { problems, priceProblems, pricePoints };
};

const AddOnDrawer = ({
  open,
  initial,
  takenNames,
  ticketTypeNames,
  showActivityNames = [],
  manualTaxes = [],
  canUseDynamicPricing = false,
  dynamicPricingDefault = false,
  defaultCheckInPeriod = "all_time",
  timedEntry = false,
  feeQuoteUrl,
  venueOwnershipId,
  csrfToken,
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState(blankAddOn);
  const [seeded, setSeeded] = useState(false);
  const [quote, setQuote] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const quoteTimer = useRef(null);
  const adapterOptions = { manualTaxes, defaultCheckInPeriod, editing: Boolean(initial), imagePreviewUrl: previewUrl };

  const recalculate = (next) => {
    if (!feeQuoteUrl) return;
    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(() => {
      fetchFeeQuote({ url: feeQuoteUrl, csrfToken, venueOwnershipId, kind: "add_on", form: next })
        .then(setQuote)
        .catch(() => toastError("Failed to calculate add-on costs"));
    }, 500);
  };

  useEffect(() => {
    if (open) {
      const next = initial
        ? { ...blankAddOn(), ...initial, dynamicPricingEnabled: canUseDynamicPricing && Boolean(initial.dynamicPricingEnabled) }
        : { ...blankAddOn(), dynamicPricingEnabled: canUseDynamicPricing && dynamicPricingDefault };
      setQuote({});
      setDraft(next);
      setSeeded(true);
      recalculate(addOnToForm(next, adapterOptions));
    } else {
      setSeeded(false);
    }
  }, [open, initial]);
  useEffect(() => () => clearTimeout(quoteTimer.current), []);

  useEffect(() => {
    if (!draft.imageFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(draft.imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [draft.imageFile]);

  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const { problems, priceProblems, pricePoints } = addOnProblems(draft, { takenNames, timedEntry, canUseDynamicPricing });
  const canSave = Object.values(problems).every((problem) => !problem);
  const paid = Number(draft.price) > 0;
  const ownWindow = Boolean(draft.startActivity || draft.endActivity);
  const ownScanWindow = Boolean(draft.scanStartActivity || draft.scanEndActivity);
  const activityOptions = showActivityNames.map((name) => ({ value: name, label: name }));

  const renderSalesWindow = () => (
    <>
      {activityOptions.length > 0 && (
        <div className="form-group">
          <Checkbox
            id="add-on-own-window"
            label="Sell on its own show details"
            checked={ownWindow}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                startActivity: event.target.checked ? "Public On-Sale" : "",
                endActivity: event.target.checked ? "Event End" : "",
              }))
            }
          />
        </div>
      )}
      {!ownWindow && <p className="ticket-drawer-muted-text">On sale from each date&apos;s public on-sale until the show ends.</p>}
      {ownWindow && (
        <div className="ticket-drawer-field-row form-group">
          <div className="ticket-drawer-field">
            <Select
              id="add-on-start-show-activity"
              label="Start Show Detail"
              placeholder="Select a show detail"
              options={activityOptions}
              value={draft.startActivity || null}
              onChange={(value) => setDraft((current) => ({ ...current, startActivity: value || "", startOffsetMinutes: 0 }))}
            />
          </div>
          <div className="ticket-drawer-field">
            <Select
              id="add-on-end-show-activity"
              label="End Show Detail"
              placeholder="Select a show detail"
              options={activityOptions}
              value={draft.endActivity || null}
              onChange={(value) => setDraft((current) => ({ ...current, endActivity: value || "", endOffsetMinutes: 0 }))}
            />
          </div>
        </div>
      )}
      {ownWindow && draft.startActivity && (
        <ShowDetailOffset label="Starts" anchorName={draft.startActivity} minutes={draft.startOffsetMinutes} onChange={(minutes) => set("startOffsetMinutes", minutes)} />
      )}
      {ownWindow && draft.endActivity && (
        <ShowDetailOffset label="Ends" anchorName={draft.endActivity} minutes={draft.endOffsetMinutes} onChange={(minutes) => set("endOffsetMinutes", minutes)} />
      )}
      {paid && (
        <PriceChangesSection
          pricePoints={pricePoints}
          showActivityNames={showActivityNames}
          problems={priceProblems}
          onChange={(next) => set("pricePoints", next)}
        />
      )}
    </>
  );

  const renderCheckInWindow = () =>
    !timedEntry && activityOptions.length > 0 ? (
      <>
        <div className="form-group">
          <Checkbox
            id="add-on-scan-window"
            label="Timed entry: only scan this add-on in between two show details"
            checked={ownScanWindow}
            onChange={(event) => {
              const { checked } = event.target;
              setDraft((current) => ({
                ...current,
                scanStartActivity: checked ? "Doors" : "",
                scanEndActivity: checked ? "Event End" : "",
              }));
            }}
          />
        </div>
        {ownScanWindow && (
          <>
            <div className="ticket-drawer-field-row form-group">
              <div className="ticket-drawer-field">
                <Select
                  label="Scanning opens"
                  placeholder="Select a show detail"
                  options={activityOptions}
                  value={draft.scanStartActivity || null}
                  onChange={(value) => setDraft((current) => ({ ...current, scanStartActivity: value || "", scanStartOffsetMinutes: 0 }))}
                />
              </div>
              <div className="ticket-drawer-field">
                <Select
                  label="Scanning closes"
                  placeholder="Select a show detail"
                  options={activityOptions}
                  value={draft.scanEndActivity || null}
                  onChange={(value) => setDraft((current) => ({ ...current, scanEndActivity: value || "", scanEndOffsetMinutes: 0 }))}
                />
              </div>
            </div>
            {draft.scanStartActivity && (
              <ShowDetailOffset label="Scanning opens" anchorName={draft.scanStartActivity} minutes={draft.scanStartOffsetMinutes} onChange={(minutes) => set("scanStartOffsetMinutes", minutes)} />
            )}
            {draft.scanEndActivity && (
              <ShowDetailOffset label="Scanning closes" anchorName={draft.scanEndActivity} minutes={draft.scanEndOffsetMinutes} onChange={(minutes) => set("scanEndOffsetMinutes", minutes)} />
            )}
            {problems.scan && <p className="ticket-drawer-error-text">{problems.scan}</p>}
          </>
        )}
      </>
    ) : null;

  // Mount the shared drawer only once this open's draft is in: it reads Paid
  // or Free from the add-on once, as it mounts.
  if (!open || !seeded) return null;

  const windowLabel = ownWindow ? `${draft.startActivity || "Start not set"} → ${draft.endActivity || "End not set"}` : "Public on-sale → show end";
  const priceChangeCount = pricePoints.length;

  return (
    <ConfirmAddOnDrawer
      addOnForForm={{ ...addOnToForm(draft, adapterOptions), ...quote }}
      confirm={{ check_in_period: defaultCheckInPeriod, venue_ownership: {} }}
      team={{ package: { dynamic_pricing: canUseDynamicPricing } }}
      showActivities={[]}
      ticketTypes={ticketTypeNames.map((name) => ({ id: name, name }))}
      manualTaxes={manualTaxes}
      manualTaxShouldBeChecked={(form, tax) => {
        const ids = manualTaxIdsFrom(form.taxable_items, manualTaxes);
        return ids == null || ids.includes(tax.id);
      }}
      canSave={canSave}
      nameError={problems.name && problems.name !== "missing" ? problems.name : undefined}
      salesWindowSummary={[windowLabel, priceChangeCount > 0 ? `${priceChangeCount} price change${priceChangeCount === 1 ? "" : "s"}` : null].filter(Boolean).join(" · ")}
      renderSalesWindow={renderSalesWindow}
      renderCheckInWindow={renderCheckInWindow}
      onChange={(next) => setDraft((current) => formToAddOn(next, current, adapterOptions))}
      onRecalculate={recalculate}
      onImageSelected={(file) => setDraft((current) => ({ ...current, imageFile: file, imageFileName: file.name, removeImage: false }))}
      onImageRemove={() => setDraft((current) => ({ ...current, imageFile: null, imageUrl: null, imageFileName: null, removeImage: true }))}
      onRemovePricePoint={() => {}}
      onSave={() => onSave({ ...draft, name: draft.name.trim(), pricePoints })}
      onClose={onCancel}
    />
  );
};

AddOnDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.object,
  takenNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  ticketTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  showActivityNames: PropTypes.arrayOf(PropTypes.string),
  manualTaxes: PropTypes.arrayOf(PropTypes.object),
  canUseDynamicPricing: PropTypes.bool,
  dynamicPricingDefault: PropTypes.bool,
  defaultCheckInPeriod: PropTypes.string,
  timedEntry: PropTypes.bool,
  feeQuoteUrl: PropTypes.string,
  venueOwnershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  csrfToken: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default AddOnDrawer;
